import { NotifyError } from './functions';

// Same allowlist as Leads (leadFormModal/basicInfoTab.js) and Maintenance
// (maintenance/components/WorkOrderFormModal.js).
export const ALLOWED_ATTACHMENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'text/csv',
];

export const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024; // 5MB per file, matching Leads/Maintenance

// Safety ceiling, not the rule users are meant to hit — 5MB per file is. Files are
// stored as base64 (≈1.33x inflation) inside the document, and MongoDB hard-rejects
// anything over 16MB, so without a total cap four 5MB files would fail the save with
// a raw driver error. 10MB raw is ~13.3MB encoded, leaving headroom.
export const MAX_ATTACHMENT_TOTAL_SIZE = 10 * 1024 * 1024;

export const ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.csv';

export const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Shape matches SupportTicket.attachments, which the detail pages already render.
// `path` holds the base64 data URI (the model was written for a Firebase Storage URL,
// but nothing in this app uploads to Firebase — see the Leads/Maintenance pattern).
const readFileAsAttachment = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
        filename: file.name,
        originalName: file.name,
        path: reader.result,
        size: file.size,
        mimetype: file.type,
        uploadedAt: new Date().toISOString(),
    });
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

/**
 * Validate and read picked files, rejecting anything oversized or of the wrong type.
 * Returns the newly read attachments, or [] if nothing survived validation.
 *
 * @param {File[]} files      Files from the input element.
 * @param {object[]} existing Attachments already staged, so the total cap accounts for them.
 */
export const readAttachments = async (files, existing = []) => {
    const valid = files.filter((file) => {
        if (file.size > MAX_ATTACHMENT_SIZE) {
            NotifyError(`${file.name} is too large. Maximum size allowed is 5MB.`);
            return false;
        }
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
            NotifyError(`${file.name}: invalid file type. Only PDF, Word, JPEG, PNG, and CSV are allowed.`);
            return false;
        }
        return true;
    });

    if (!valid.length) return [];

    const existingTotal = existing.reduce((sum, att) => sum + (att.size || 0), 0);
    const incomingTotal = valid.reduce((sum, file) => sum + file.size, 0);

    if (existingTotal + incomingTotal > MAX_ATTACHMENT_TOTAL_SIZE) {
        NotifyError(
            `That would bring your attachments to ${formatFileSize(existingTotal + incomingTotal)}, ` +
            `over the ${formatFileSize(MAX_ATTACHMENT_TOTAL_SIZE)} limit for one ticket or message. ` +
            `Remove a file, or send the rest in a follow-up reply.`
        );
        return [];
    }

    return Promise.all(valid.map(readFileAsAttachment));
};
